import { computed, nextTick, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { difference, groupBy, intersection, sortBy, uniq } from 'lodash';

import type {
  BookableTransportIngredient,
  RecipeIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  HotelIngredient,
  ActivityIngredient,
  isHotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  isOwnStayIngredient,
  isOwnTransportIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import type { Result } from '@libs/utils';
import {
  assertValue,
  exhaustiveGuard,
  fail,
  getDifferenceInDays,
  getHotelKey,
  getLongDatePeriod,
  getShortDatePeriod,
  isDefined,
  isFailure,
  isSuccess,
  readonlyRef,
  replaceOrDeleteValues,
  succeed,
  ToggleMode,
} from '@libs/utils';
import type {
  Polygon,
  OverviewHeartedStaticInfo,
  PortStaticInfo,
  TransportLeg,
  IngredientId,
  ActivityTicket,
  DestinationStaticInfo,
  ChronologicalItinerary,
  CarrierName,
  OverviewDestinationStaticInfo,
  HeartedHotel,
  DetailsStaticInfo,
  TransportStaticInfo,
  FlightIngredientResponse,
  FerryIngredientResponse,
  CarrierStaticInfo,
  AvailableFlightIngredientResponse,
  AvailableTransportIngredientResponseWithStaticInfo,
  DestinationProduct,
  ProductCheapestAvailabilityResponse,
  BoardBasisCode,
  ItineraryWithStaticInfo,
  HotelIngredientResponseWithStaticInfo,
  OwnStayIngredientResponseWithStaticInfo,
  ItineraryMainIngredientResponse,
  TransportIngredientResponseWithStaticInfo,
  HotelIngredientResponse,
  OwnStayIngredientResponse,
  ActivityIngredientResponseWithStaticInfo,
  OverviewActivityStaticInfo,
  HotelAlternativeOptionWithBasicRooms,
  TransportVehicle,
  HotelBookableOptionWithRooms,
  ItineraryMainIngredientResponseWithStaticInfo,
  OwnTransportIngredientResponseWithStaticInfo,
  OwnTransportIngredientResponse,
  ItineraryWithPrice,
} from '@libs/trip-planner-interfaces';
import {
  SearchTermType,
  PolygonType,
  IngredientType,
  TransportType,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';
import {
  isAvailableActivityIngredientResponse,
  isAvailableHotelIngredientResponse,
  isAvailableFlightIngredientResponse,
  isAvailableTransportIngredientResponse,
  isAvailableTransportIngredientResponseWithStaticInfo,
  isHotelIngredientResponse,
  isHotelIngredientResponseWithStaticInfo,
  isOwnStayIngredientResponse,
  isOwnStayIngredientResponseWithStaticInfo,
  isOwnTransportIngredientResponse,
  isStayIngredientResponseWithStaticInfo,
  isTransportIngredientResponse,
  LOCAL_SESSION_ID,
} from '@libs/search-service-smart-search';
import { getIsSmallDevice } from '@libs/ui';
import { trackMonthChanged } from '@libs/google-analytics-events';

import { useRecipeStore } from '../recipe/recipeStore';
import { useEditingIngredientStore } from '../editingIngredient/editingIngredientStore';
import { useItineraryStore } from '../itinerary/itineraryStore';
import { usePreferredTimeStore } from '../preferredTimeStore';
import { useStaticInfoStore } from '../staticInfo/staticInfoStore';
import { useMetaStore } from '../meta/metaStore';

import type {
  FlightRanges,
  MainIngredientResponseWithStaticInfo,
  MissingIngredientResponseStaticInfoError,
  MissingOwnTransportStaticInfoError,
} from './tripStore.model';
import {
  MissingItineraryAvailabilityError,
  MissingOwnStayStaticInfoError,
  MissingActivitiesStaticInfoError,
  MissingHotelStaticInfoError,
  MissingTransportStaticInfoError,
  MissingItineraryStaticInfoError,
  MissingTransportPortStaticInfoError,
  MissingTransportCarrierStaticInfoError,
} from './tripStore.model';
import {
  getHeartedRoomMatchers,
  getTravelDateRange,
  getTransportRouteKey,
  getWidestRange,
  stayRoomMatchesHeartedRoom,
} from './tripStore.logic';

interface DatePeriodForStopParams {
  format: 'long' | 'short';
  stop: ChronologicalItinerary;
  isLastStop?: boolean;
  isSmallDevice?: boolean;
}

export const useTripStore = defineStore('TripStore', () => {
  const itineraryStore = useItineraryStore();
  const recipeStore = useRecipeStore();
  const editingIngredientStore = useEditingIngredientStore();
  const preferredTimeStore = usePreferredTimeStore();
  const staticInfoStore = useStaticInfoStore();
  const metaStore = useMetaStore();
  const { startSearchDate, endSearchDate, preferredDay, expandedMonth } =
    storeToRefs(preferredTimeStore);
  const { clearPreferredDay, expandMonth, isDateInExpandedMonth } =
    preferredTimeStore;

  // State
  const isLocationManuallyChanged = ref<boolean>(false);
  const areAllSelectedRatesValid = ref<boolean>(true);
  const startingLocationIsManuallyTyped = ref(false);
  const endLocationIsManuallyTyped = ref<boolean>(false);
  const destinationAvailabilitySearchCounter = ref<number>(0);
  const staticDestinationAvailabilitySearchCounter = ref<number>(0);
  const hotelSelectionItinerarySearchCounter = ref<number>(0);
  const hotelSelectionStaticInfoSearchCounter = ref<number>(0);
  const hotelSelectionItinerary = ref<ItineraryWithPrice>();
  const staysShown = ref<number>(0);
  const transportListSearchCounter = ref<number>(0);
  const sessionId = ref<string>(LOCAL_SESSION_ID);
  const destinationWithAvailableActivities = ref<Record<string, boolean>>({});
  const months = ref<Date[]>(SemboDate.getNextMonthsArray());
  const ferryVehicleTypes = ref<TransportVehicle[]>();
  const newDestinationStaticInfo = ref<Record<string, DestinationStaticInfo>>(
    {}
  );
  const newDestinationAvailability = ref<
    Record<string, ProductCheapestAvailabilityResponse>
  >({});
  const destinationStaticInfo = ref<Record<string, DestinationStaticInfo>>({});
  const destinationAvailability = ref<
    Record<string, ProductCheapestAvailabilityResponse>
  >({});

  const transportList = ref<
    AvailableTransportIngredientResponseWithStaticInfo[]
  >([]);

  const lastKnownCarrierCodesCache = ref<
    Record<IngredientId, { routeKey: string; allCarrierCodes: string[] }>
  >({});

  // TODO: move to itineraryStore when staticInfoStore is ready
  const hotelsInItinerary = computed<OverviewDestinationStaticInfo[]>(() => {
    if (!itineraryStore.selectedItinerary) {
      return [];
    }

    return itineraryStore.selectedItinerary.hotelOnlyItinerary
      .map((ingredientResponse) =>
        isAvailableHotelIngredientResponse(ingredientResponse)
          ? getOverviewStaticInfo(
              ingredientResponse.suggested.code,
              ingredientResponse.ingredientId
            )
          : undefined
      )
      .filter(isDefined);
  });

  const preselectedHotel = computed(() => {
    const hotel = hotelsInItinerary.value.find(
      (hotel) =>
        hotel.ingredientId ===
        editingIngredientStore.editingStayIngredient?.ingredientId
    );
    return hotel
      ? {
          ...hotel,
          isHearted: hotelIsHearted(hotel.hotelCode, hotel.ingredientId),
        }
      : undefined;
  });

  const chronologicalItinerary = computed<ChronologicalItinerary[]>(() => {
    if (!selectedItineraryWithStaticInfo.value) {
      return [];
    }
    const tripStartDate = selectedItineraryWithStaticInfo.value.date;

    const fullItinerary = addUnknownOwnTransport(
      selectedItineraryWithStaticInfo.value.itinerary
    );

    return Object.entries(
      groupBy(fullItinerary, (ingredient) =>
        getBaseKeyForIngredient(ingredient)
      )
    )
      .sort(sortEntriesByDate)
      .map(([startDateStr, ingredients]) => {
        const cleanedDateStr = startDateStr.replace(/-\d+$/, '');
        const startDate = new Date(cleanedDateStr);

        const sortedIngredients = sortIngredientsByTransport(ingredients);

        const { dayPeriod, datePeriod, shortDatePeriod } = getDayAndDatePeriods(
          sortedIngredients,
          startDate,
          tripStartDate
        );

        const stayIngredientInGroup = sortedIngredients.find(
          isStayIngredientResponseWithStaticInfo
        );
        const numberOfNights = stayIngredientInGroup?.numberOfNights ?? 0;

        return {
          startDate,
          numberOfNights,
          ingredients: sortedIngredients,
          datePeriod,
          dayPeriod,
          shortDatePeriod,
        };
      });
  });

  const destinationProducts = computed<DestinationProduct[]>(() => {
    const editingHotel = editingIngredientStore.editingStayIngredient;
    if (!destinationStaticInfo.value || !editingHotel) {
      return [];
    }

    // NOTE: THIS IS A FIX/HACK/WORKAROUND FOR BUG IN INSIGHT NOT RETURNING AVAIL FOR SUGGESTED HOTEL
    const ingredientResponseWithStaticInfo =
      selectedItineraryWithStaticInfo.value?.hotelOnlyItinerary.find(
        (itinerary) => itinerary.ingredientId === editingHotel.ingredientId
      );
    const suggestedHotelCode =
      ingredientResponseWithStaticInfo?.suggested?.code;
    const suggestedHotelTotalPrice =
      ingredientResponseWithStaticInfo?.suggested?.totalPrice;
    // END NOTE

    return Object.values(destinationStaticInfo.value)
      .filter((hotel) => hotel.ingredientId === editingHotel.ingredientId)
      .map<DestinationProduct>((hotel) => ({
        ...hotel,
        isHearted: hotelIsHearted(hotel.hotelCode, hotel.ingredientId),
        totalPrice:
          suggestedHotelCode === hotel.hotelCode && suggestedHotelTotalPrice
            ? suggestedHotelTotalPrice
            : destinationAvailability.value[hotel.hotelCode]?.prices.totalPrice,
        grossPrice:
          destinationAvailability.value[hotel.hotelCode]?.prices.grossPrice,
        boardBasis: getRoomsBoardBasis(hotel.hotelCode),
        isPriceWithTaxes:
          isFeatureToggleActive('show-price-with-taxes') ||
          !!destinationAvailability.value[hotel.hotelCode]?.prices
            .priceIsWithTaxesIncluded,
      }));
  });

  const isSearchingDestinationAvailability = computed<boolean>(
    () =>
      destinationAvailabilitySearchCounter.value > 0 ||
      staticDestinationAvailabilitySearchCounter.value > 0
  );

  const isSearchingHotelSelectionItinerary = computed<boolean>(
    () =>
      hotelSelectionItinerarySearchCounter.value > 0 ||
      hotelSelectionStaticInfoSearchCounter.value > 0
  );

  const selectedItineraryWithStaticInfoResults = computed<
    Result<
      {
        itineraryAvailability: ItineraryWithPrice;
        ingredientsResponseWithStaticInfo: MainIngredientResponseWithStaticInfo[];
      },
      MissingItineraryStaticInfoError | MissingItineraryAvailabilityError
    >
  >(() => {
    if (!itineraryStore.selectedItinerary) {
      return fail(new MissingItineraryAvailabilityError());
    }

    const ingredientResponseWithStaticInfoResults =
      itineraryStore.selectedItinerary.itinerary.map(
        getIngredientResponseWithStaticInfo
      );

    const successfulIngredientResponseWithStaticInfoResults =
      ingredientResponseWithStaticInfoResults.filter(isSuccess);

    const areAllIngredientResponsesWithStaticInfoSuccessful =
      successfulIngredientResponseWithStaticInfoResults.length ===
      ingredientResponseWithStaticInfoResults.length;

    if (areAllIngredientResponsesWithStaticInfoSuccessful) {
      return succeed({
        itineraryAvailability: itineraryStore.selectedItinerary,
        ingredientsResponseWithStaticInfo:
          successfulIngredientResponseWithStaticInfoResults.map(
            (success) => success.data
          ),
      });
    }

    return fail(
      new MissingItineraryStaticInfoError(
        ingredientResponseWithStaticInfoResults
          .filter(isFailure)
          .map((failure) => failure.error)
      )
    );
  });

  // TODO: move to itineraryStore when staticInfoStore is ready
  const selectedItineraryWithStaticInfo = computed<
    ItineraryWithStaticInfo | undefined
  >(() => {
    if (isFailure(selectedItineraryWithStaticInfoResults.value)) {
      return;
    }

    const itineraryWithStaticInfo =
      selectedItineraryWithStaticInfoResults.value.data
        .ingredientsResponseWithStaticInfo;

    const ownStayItineraryWithStaticInfo = itineraryWithStaticInfo.filter(
      isOwnStayIngredientResponseWithStaticInfo
    );

    const hotelItineraryWithStaticInfo = itineraryWithStaticInfo.filter(
      isHotelIngredientResponseWithStaticInfo
    );

    return {
      ...selectedItineraryWithStaticInfoResults.value.data
        .itineraryAvailability,
      itinerary: itineraryWithStaticInfo,
      ownStayOnlyItinerary: ownStayItineraryWithStaticInfo,
      hotelOnlyItinerary: hotelItineraryWithStaticInfo,
    };
  });

  const travelDateRange = computed<string>(() =>
    getTravelDateRange(chronologicalItinerary.value)
  );

  const isSearchingTransportList = computed<boolean>(
    () => transportListSearchCounter.value > 0
  );

  // Actions
  function getBaseKeyForIngredient(
    ingredient: ItineraryMainIngredientResponseWithStaticInfo
  ): string {
    const baseKey = new SemboDate(ingredient.date)
      .startOf('day')
      .get()
      .toISOString();

    return baseKey;
  }

  function getRoomsBoardBasis(code: string): BoardBasisCode[] {
    return (
      destinationAvailability.value[code]?.rooms
        .map((room) => room.boardBasis)
        .filter(isDefined) ?? []
    );
  }

  function getDatePeriodForStop(params: DatePeriodForStopParams): string {
    let stopDuration = params.stop.numberOfNights;
    params.stop.ingredients.forEach((ingredient, index, originalArray) => {
      if (
        isAvailableTransportIngredientResponseWithStaticInfo(ingredient) &&
        index === originalArray.length - 1
      ) {
        const transportDuration = getTransportDuration(ingredient);
        stopDuration += transportDuration;
      }
    });
    return params.format === 'long'
      ? getLongDatePeriod(
          params.stop.startDate,
          new SemboDate(params.stop.startDate)
            .add({ days: stopDuration })
            .get(),
          params.isLastStop
        )
      : getShortDatePeriod(
          params.stop.startDate,
          new SemboDate(params.stop.startDate)
            .add({ days: stopDuration })
            .get(),
          params.isLastStop,
          params.isSmallDevice
        );
  }

  function getTransportDuration(
    transportIngredient: AvailableTransportIngredientResponseWithStaticInfo
  ): number {
    const transportDuration = getDifferenceInDays(
      transportIngredient.arrivalDate,
      transportIngredient.departureDate
    );
    return transportDuration;
  }

  function getFerryAllCarriers(ferryIngredientId: IngredientId): {
    allCarriers: CarrierName[];
    areAllCarriersAvailable: boolean;
  } {
    return getAllCarriers(ferryIngredientId, getFerryAllCarrierCodes);
  }

  function recallCarrierCodes(
    ingredientId: IngredientId,
    routeKey: string
  ): string[] {
    const remembered = lastKnownCarrierCodesCache.value[ingredientId];

    return remembered?.routeKey === routeKey ? remembered.allCarrierCodes : [];
  }

  function getTransportResponsesFromItineraries<
    T extends FerryIngredientResponse | FlightIngredientResponse,
  >(
    ingredientId: IngredientId,
    getItineraryTransports: (itinerary: ItineraryWithPrice) => T[]
  ): T[] {
    const itineraries = preferredDay.value
      ? [itineraryStore.selectedItinerary]
      : itineraryStore.itineraries;

    return itineraries
      .filter(isDefined)
      .map((itinerary) =>
        getItineraryTransports(itinerary).find(
          (response) => response.ingredientId === ingredientId
        )
      )
      .filter(isDefined);
  }

  function getTransportAllCarrierCodes<
    T extends FerryIngredientResponse | FlightIngredientResponse,
  >(
    ingredientId: IngredientId,
    getIngredient: (id: IngredientId) => BookableTransportIngredient,
    getItineraryTransports: (itinerary: ItineraryWithPrice) => T[]
  ): {
    allCarrierCodes: string[];
    areAllCarrierCodesAvailable: boolean;
  } {
    let carrierFilters: string[];
    let routeKey: string;

    try {
      const ingredient = getIngredient(ingredientId);

      carrierFilters = ingredient.filters.carrierFilters;
      routeKey = getTransportRouteKey(ingredient);
    } catch {
      return {
        allCarrierCodes: [],
        areAllCarrierCodesAvailable: true,
      };
    }

    if (!itineraryStore.selectedItinerary) {
      const rememberedCarrierCodes = recallCarrierCodes(ingredientId, routeKey);

      return {
        allCarrierCodes: uniq([...carrierFilters, ...rememberedCarrierCodes]),
        areAllCarrierCodesAvailable:
          !itineraryStore.isSearchingItineraries ||
          rememberedCarrierCodes.length > 0,
      };
    }

    const responses = getTransportResponsesFromItineraries(
      ingredientId,
      getItineraryTransports
    );
    const responseCarrierCodes = responses.flatMap((response) =>
      isAvailableTransportIngredientResponse(response)
        ? response.allCarriers
        : (response.alternativeSuggestion?.carriers ?? [])
    );

    if (!responses.some(isAvailableTransportIngredientResponse)) {
      return {
        allCarrierCodes: uniq([
          ...carrierFilters,
          ...responseCarrierCodes,
          ...recallCarrierCodes(ingredientId, routeKey),
        ]),
        areAllCarrierCodesAvailable: true,
      };
    }

    const allCarrierCodes = uniq([...carrierFilters, ...responseCarrierCodes]);

    lastKnownCarrierCodesCache.value[ingredientId] = {
      routeKey,
      allCarrierCodes,
    };

    return { allCarrierCodes, areAllCarrierCodesAvailable: true };
  }

  function getFerryAllCarrierCodes(ferryIngredientId: IngredientId): {
    allCarrierCodes: string[];
    areAllCarrierCodesAvailable: boolean;
  } {
    return getTransportAllCarrierCodes(
      ferryIngredientId,
      recipeStore.getFerryIngredient,
      (itinerary) => itinerary.ferryOnlyItinerary
    );
  }

  function getFlightAllCarriers(flightIngredientId: IngredientId): {
    allCarriers: CarrierName[];
    areAllCarriersAvailable: boolean;
  } {
    return getAllCarriers(flightIngredientId, getFlightAllCarrierCodes);
  }

  function getAllCarriers(
    ingredientId: IngredientId,
    getAllCarrierCodes: (ingredientId: IngredientId) => {
      allCarrierCodes: string[];
      areAllCarrierCodesAvailable: boolean;
    }
  ): {
    allCarriers: CarrierName[];
    areAllCarriersAvailable: boolean;
  } {
    const { allCarrierCodes, areAllCarrierCodesAvailable } =
      getAllCarrierCodes(ingredientId);

    const allCarrierNames = allCarrierCodes.map<CarrierName | undefined>(
      (carrierCode) => staticInfoStore.carrierNames[carrierCode]
    );

    const areAllCarrierNamesAvailable = allCarrierNames.every(isDefined);

    return {
      allCarriers: allCarrierNames.filter(isDefined),
      areAllCarriersAvailable:
        areAllCarrierCodesAvailable && areAllCarrierNamesAvailable,
    };
  }

  function getFlightAllCarrierCodes(flightIngredientId: IngredientId): {
    allCarrierCodes: string[];
    areAllCarrierCodesAvailable: boolean;
  } {
    return getTransportAllCarrierCodes(
      flightIngredientId,
      recipeStore.getFlightIngredient,
      (itinerary) => itinerary.flightOnlyItinerary
    );
  }

  function getMainPolygonFullName(
    parentIngredientId: IngredientId,
    staticInfo: DetailsStaticInfo
  ): string | undefined {
    const ingredient =
      selectedItineraryWithStaticInfo.value?.hotelOnlyItinerary.find(
        (hotel) => hotel.ingredientId === parentIngredientId
      );
    const foundPolygon = staticInfo.polygons.find(
      (polygon) => polygon.polygonId === ingredient?.staticInfo?.mainPolygon?.id
    );

    if (!foundPolygon) {
      return;
    }

    return ingredient?.staticInfo?.mainPolygon?.fullName;
  }

  function getMainPolygonId(
    parentIngredientId: IngredientId
  ): number | undefined {
    const ingredient =
      selectedItineraryWithStaticInfo.value?.hotelOnlyItinerary.find(
        (hotel) => hotel.ingredientId === parentIngredientId
      );
    return ingredient?.staticInfo?.mainPolygon?.id;
  }

  // TODO: move to itineraryStore when staticInfoStore is ready
  function getIngredientResponseWithStaticInfo(
    ingredientResponse: ItineraryMainIngredientResponse
  ): Result<
    MainIngredientResponseWithStaticInfo,
    MissingIngredientResponseStaticInfoError
  > {
    if (isTransportIngredientResponse(ingredientResponse)) {
      return getTransportIngredientResponseWithStaticInfo(ingredientResponse);
    }

    if (isHotelIngredientResponse(ingredientResponse)) {
      return getHotelIngredientResponseWithStaticInfo(ingredientResponse);
    }

    if (isOwnStayIngredientResponse(ingredientResponse)) {
      return getOwnStayIngredientResponseWithStaticInfo(ingredientResponse);
    }

    if (isOwnTransportIngredientResponse(ingredientResponse)) {
      return getOwnTransportIngredientResponseWithStaticInfo(
        ingredientResponse
      );
    }

    exhaustiveGuard(
      ingredientResponse,
      'Unhandles ingredient response static info'
    );
  }

  function getOwnStayIngredientResponseWithStaticInfo(
    ingredientResponse: OwnStayIngredientResponse
  ): Result<
    OwnStayIngredientResponseWithStaticInfo,
    MissingOwnStayStaticInfoError | MissingActivitiesStaticInfoError
  > {
    const subIngredientResponseWithStaticInfoResult =
      getActivityIngredientResponseWithStaticInfo(ingredientResponse);

    if (isFailure(subIngredientResponseWithStaticInfoResult)) {
      return subIngredientResponseWithStaticInfoResult;
    }

    let ingredient: RecipeIngredient;

    try {
      ingredient = recipeStore.getIngredient(ingredientResponse.ingredientId);
    } catch {
      return fail(new MissingOwnStayStaticInfoError());
    }

    const transportToBeReached = ingredient.transportTypeToBeReached;

    return succeed({
      ...ingredientResponse,
      transportToBeReached,
      staticInfo: null,
      subIngredientResponseWithStaticInfo:
        subIngredientResponseWithStaticInfoResult.data,
    });
  }

  function getOwnTransportIngredientResponseWithStaticInfo(
    ingredientResponse: OwnTransportIngredientResponse
  ): Result<
    OwnTransportIngredientResponseWithStaticInfo,
    MissingOwnTransportStaticInfoError
  > {
    return succeed({
      ...ingredientResponse,
      staticInfo: null,
    });
  }

  function getTransportIngredientResponseWithStaticInfo(
    ingredientResponse: FlightIngredientResponse | FerryIngredientResponse
  ): Result<
    TransportIngredientResponseWithStaticInfo,
    MissingTransportStaticInfoError
  > {
    if (!isAvailableTransportIngredientResponse(ingredientResponse)) {
      if (ingredientResponse.alternativeSuggestion) {
        const staticInfoResult =
          getTransportStaticInfoResult(ingredientResponse);

        if (isFailure(staticInfoResult)) {
          const missingPorts =
            staticInfoResult.error instanceof
            MissingTransportPortStaticInfoError
              ? staticInfoResult.error.misingPorts
              : [];
          const missingCarriers =
            staticInfoResult.error instanceof
            MissingTransportCarrierStaticInfoError
              ? staticInfoResult.error.missingCarriers
              : [];

          return fail(
            new MissingTransportStaticInfoError(
              ingredientResponse.ingredientType,
              ingredientResponse.date,
              'alternative',
              ingredientResponse.alternativeSuggestion.transportIds,
              missingPorts,
              missingCarriers
            )
          );
        }

        return succeed({
          ...ingredientResponse,
          staticInfo: staticInfoResult.data,
        });
      } else {
        return succeed({
          ...ingredientResponse,
          staticInfo: undefined,
        });
      }
    }

    const staticInfoResult = getTransportStaticInfoResult(ingredientResponse);

    if (isFailure(staticInfoResult)) {
      const missingPorts =
        staticInfoResult.error instanceof MissingTransportPortStaticInfoError
          ? staticInfoResult.error.misingPorts
          : [];
      const missingCarriers =
        staticInfoResult.error instanceof MissingTransportCarrierStaticInfoError
          ? staticInfoResult.error.missingCarriers
          : [];

      return fail(
        new MissingTransportStaticInfoError(
          ingredientResponse.ingredientType,
          ingredientResponse.date,
          'available',
          ingredientResponse.transportIds,
          missingPorts,
          missingCarriers
        )
      );
    }

    return succeed({
      ...ingredientResponse,
      staticInfo: staticInfoResult.data,
    });
  }

  function getHotelIngredientResponseWithStaticInfo(
    ingredientResponse: HotelIngredientResponse
  ): Result<
    HotelIngredientResponseWithStaticInfo,
    MissingHotelStaticInfoError | MissingActivitiesStaticInfoError
  > {
    const subIngredientResponseWithStaticInfoResult =
      getActivityIngredientResponseWithStaticInfo(ingredientResponse);

    if (isFailure(subIngredientResponseWithStaticInfoResult)) {
      return subIngredientResponseWithStaticInfoResult;
    }

    /**
     * Try/catch prevents error on localhost (rarely on production) when removing a destination,
     * because selectedItineraryWithStaticInfo (computed) is accessed twice
     * and the second time the ingredient to be removed does no longer exists so it cannot be found.
     * But does not work when removing the last destination (still better than before)
     *
     * There is a fix on this branch: https://bitbucket.org/sembo-it/trip-planner-front/branch/bugfix/remove-destination
     * but produces another bug described in trello card: https://trello.com/c/Jjc3tNJ0
     */
    let ingredient: RecipeIngredient;

    try {
      ingredient = recipeStore.getIngredient(ingredientResponse.ingredientId);
    } catch {
      return fail(new MissingHotelStaticInfoError(ingredientResponse.date));
    }

    const transportToBeReached = ingredient.transportTypeToBeReached;

    if (!isAvailableHotelIngredientResponse(ingredientResponse)) {
      const suggestionsStaticInfo = ingredientResponse.suggestions.reduce(
        (
          acc: OverviewDestinationStaticInfo[],
          suggestion: HotelAlternativeOptionWithBasicRooms
        ) => {
          const suggestionStaticInfo = getOverviewStaticInfo(
            suggestion.code,
            ingredientResponse.ingredientId
          );
          if (suggestionStaticInfo) {
            acc.push(suggestionStaticInfo);
          }
          return acc;
        },
        []
      );

      if (ingredientResponse.alternativeSuggestion?.code) {
        const staticInfo = getOverviewStaticInfo(
          ingredientResponse.alternativeSuggestion.code,
          ingredientResponse.ingredientId
        );

        if (!staticInfo) {
          return fail(
            new MissingHotelStaticInfoError(
              ingredientResponse.date,
              ingredientResponse.alternativeSuggestion.code,
              'alternative'
            )
          );
        }

        return succeed({
          ...ingredientResponse,
          transportToBeReached,
          staticInfo,
          suggestionsStaticInfo,
          subIngredientResponseWithStaticInfo:
            subIngredientResponseWithStaticInfoResult.data,
        });
      } else {
        const hotelIngredient = ingredient as HotelIngredient;
        const selectedHotelCode = hotelIngredient.heartedHotelCodes[0];
        const staticInfo = selectedHotelCode
          ? getOverviewStaticInfo(
              selectedHotelCode,
              ingredientResponse.ingredientId
            )
          : undefined;

        return succeed({
          ...ingredientResponse,
          transportToBeReached,
          staticInfo,
          suggestionsStaticInfo,
          subIngredientResponseWithStaticInfo:
            subIngredientResponseWithStaticInfoResult.data,
        });
      }
    }

    const staticInfo = getOverviewStaticInfo(
      ingredientResponse.suggested.code,
      ingredientResponse.ingredientId
    );

    if (!staticInfo) {
      return fail(
        new MissingHotelStaticInfoError(
          ingredientResponse.date,
          ingredientResponse.suggested.code,
          'available'
        )
      );
    }

    const suggestionsStaticInfo = ingredientResponse.suggestions.reduce(
      (
        acc: OverviewDestinationStaticInfo[],
        suggestion: HotelAlternativeOptionWithBasicRooms
      ) => {
        const staticInfo = getOverviewStaticInfo(
          suggestion.code,
          ingredientResponse.ingredientId
        );

        if (staticInfo) {
          acc.push(staticInfo);
        }
        return acc;
      },
      []
    );

    return succeed({
      ...ingredientResponse,
      transportToBeReached,
      staticInfo,
      subIngredientResponseWithStaticInfo:
        subIngredientResponseWithStaticInfoResult.data,
      suggestionsStaticInfo,
    });
  }

  function getOverviewStaticInfo(
    hotelCode: string,
    ingredientId: IngredientId
  ): OverviewDestinationStaticInfo | undefined {
    return staticInfoStore.getOverviewStaticInfo(hotelCode, ingredientId);
  }

  function getActivityIngredientResponseWithStaticInfo(
    ingredientResponse: HotelIngredientResponse | OwnStayIngredientResponse
  ): Result<
    ActivityIngredientResponseWithStaticInfo[],
    MissingActivitiesStaticInfoError
  > {
    const subIngredientResponsesWithStaticInfoResults: Result<
      ActivityIngredientResponseWithStaticInfo,
      MissingActivitiesStaticInfoError
    >[] = ingredientResponse.subIngredientResponse.map((response) => {
      if (!isAvailableActivityIngredientResponse(response)) {
        if (response.alternativeSuggestion) {
          const staticInfo = getSubIngredientStaticInfo(
            response.alternativeSuggestion.code,
            response.ingredientId
          );

          if (!staticInfo) {
            return fail(
              new MissingActivitiesStaticInfoError([
                {
                  availability: 'alternative',
                  date: response.date,
                  code: response.alternativeSuggestion.code,
                },
              ])
            );
          }

          return succeed({
            ...response,
            staticInfo,
          });
        } else {
          return succeed({ ...response, staticInfo: undefined });
        }
      }

      const staticInfo = getSubIngredientStaticInfo(
        response.suggested.code,
        response.ingredientId
      );

      if (!staticInfo) {
        return fail(
          new MissingActivitiesStaticInfoError([
            {
              availability: 'available',
              date: response.date,
              code: response.suggested.code,
            },
          ])
        );
      }

      return succeed({
        ...response,
        staticInfo,
      });
    });

    const successfulSubIngredientResponsesWithStaticInfoResults =
      subIngredientResponsesWithStaticInfoResults.filter(isSuccess);

    const areAllSubIngredientResultsSuccessful =
      successfulSubIngredientResponsesWithStaticInfoResults.length ===
      subIngredientResponsesWithStaticInfoResults.length;

    if (areAllSubIngredientResultsSuccessful) {
      return succeed(
        successfulSubIngredientResponsesWithStaticInfoResults.map(
          (success) => success.data
        )
      );
    }

    return fail(
      new MissingActivitiesStaticInfoError(
        subIngredientResponsesWithStaticInfoResults
          .filter(isFailure)
          .flatMap((failure) => failure.error.activitiesWithMissingStaticInfo)
      )
    );
  }

  function getSubIngredientStaticInfo(
    code: string,
    ingredientId: IngredientId
  ): OverviewActivityStaticInfo | undefined {
    return staticInfoStore.getSubIngredientStaticInfo(code, ingredientId);
  }

  function getCommonMinimumPolygon(
    heartedHotelCodes: string[],
    ingredientId: IngredientId
  ): Polygon | undefined {
    const staticInfoFromProvidedCodes = heartedHotelCodes
      .map((productCode) =>
        getHeartedHotelStaticInfo(productCode, ingredientId)
      )
      .filter(isDefined);

    if (!staticInfoFromProvidedCodes.length) {
      return;
    }

    const polygonIdsFromProducts = staticInfoFromProvidedCodes.map((product) =>
      product.polygons.map((polygon) => polygon.polygonId)
    );

    const commonPolygonIds = intersection(...polygonIdsFromProducts);

    const firstProduct = staticInfoFromProvidedCodes[0];

    assertValue(
      firstProduct,
      'tripState.ts > getCommonMinimumPolygon > firstProduct'
    );

    const commonPolygons = firstProduct.polygons.filter((polygon) =>
      commonPolygonIds.includes(polygon.polygonId)
    );

    const sorted = sortBy(commonPolygons, (polygon) =>
      parseInt(PolygonType[polygon.type], 10)
    );

    return sorted[0];
  }

  function getHeartedHotelStaticInfo(
    hotelCode: string,
    parentIngredientId: IngredientId
  ): OverviewHeartedStaticInfo | DestinationStaticInfo | undefined {
    const hotelKey = getHotelKey(hotelCode, parentIngredientId);
    const staticInfoDictionary = editingIngredientStore.editingStayIngredient
      ? destinationStaticInfo.value
      : staticInfoStore.heartedHotelsOverviewStaticInfo;
    return staticInfoDictionary[hotelKey];
  }

  function getAvailableTransportResponsesFromItineraries(
    flightIngredientId: IngredientId
  ): AvailableFlightIngredientResponse[] {
    const responseFromSelectedItinerary =
      itineraryStore.selectedItinerary?.flightOnlyItinerary.find(
        (itinerary) => itinerary.ingredientId === flightIngredientId
      );

    if (
      !responseFromSelectedItinerary?.isAvailable ||
      itineraryStore.isSearchingItineraries
    ) {
      return [];
    }

    return getTransportResponsesFromItineraries(
      flightIngredientId,
      (itinerary) => itinerary.flightOnlyItinerary
    ).filter(isAvailableFlightIngredientResponse);
  }

  function getFlightRanges(flightIngredientId: IngredientId): FlightRanges {
    const responses =
      getAvailableTransportResponsesFromItineraries(flightIngredientId);

    return {
      duration: getWidestRange(
        responses.map((response) => response.durationRangeInMinutes)
      ),
      price: getWidestRange(responses.map((response) => response.priceRange)),
    };
  }

  function getStaticInfo(
    hotelCode: string,
    ingredientId: IngredientId
  ): DestinationStaticInfo | OverviewDestinationStaticInfo | undefined {
    if (editingIngredientStore.editingStayIngredient) {
      const staticInfoFromDestination = getDestinationStaticInfo(
        hotelCode,
        ingredientId
      );

      if (staticInfoFromDestination) {
        return staticInfoFromDestination;
      }
    }
    return getOverviewStaticInfo(hotelCode, ingredientId);
  }

  function getDestinationStaticInfo(
    code: string,
    ingredientId: IngredientId
  ): DestinationStaticInfo | undefined {
    return destinationStaticInfo.value[getHotelKey(code, ingredientId)];
  }

  function getDayPeriodForStop(
    stop: ChronologicalItinerary,
    tripStartDate?: Date,
    isLastStop?: boolean
  ): string | undefined {
    const period = getDayDurationFromStop(stop, tripStartDate);
    if (!period) {
      return;
    }
    const isSameDay = period[0] === period[1];

    return isLastStop || isSameDay
      ? `${period[1]}`
      : `${period[0]}-${period[1]}`;
  }

  function getDayDurationFromStop(
    stop: ChronologicalItinerary | undefined,
    tripStartDate?: Date
  ): number[] | undefined {
    if (!tripStartDate || !stop) {
      return;
    }
    const firstPeriodValue = 1;
    const tripDay1 = tripStartDate;
    const periodDay1 = new SemboDate(stop.startDate);
    const differenceInDays = periodDay1.diff(tripDay1, 'calendarDay');
    const firstPeriodDay = differenceInDays + firstPeriodValue;
    let lastPeriodDay = firstPeriodDay + stop.numberOfNights;

    stop.ingredients.forEach((ingredient, index, originalArray) => {
      if (
        isAvailableTransportIngredientResponseWithStaticInfo(ingredient) &&
        index === originalArray.length - 1
      ) {
        const transportDuration = getTransportDuration(ingredient);
        lastPeriodDay += transportDuration;
      }
    });

    return [firstPeriodDay, lastPeriodDay];
  }

  function getTransportStaticInfoResult(
    ingredientResponse: FerryIngredientResponse | FlightIngredientResponse
  ): Result<
    TransportStaticInfo,
    MissingTransportPortStaticInfoError | MissingTransportCarrierStaticInfoError
  > {
    const ingredientPorts = uniq(
      getPortsFromTransportResponse(ingredientResponse)
    );
    const ingredientPortsStaticInfo = getPortsStaticInfo(
      ingredientPorts,
      ingredientResponse.ingredientType
    );

    if (
      ingredientPorts.length !== Object.keys(ingredientPortsStaticInfo).length
    ) {
      return fail(
        new MissingTransportPortStaticInfoError(
          difference(ingredientPorts, Object.keys(ingredientPortsStaticInfo))
        )
      );
    }

    const ingredientCarriers = uniq(
      getCarriersFromTransportResponse(ingredientResponse)
    );
    const ingredientCarriersStaticInfo =
      getCarriersStaticInfo(ingredientCarriers);

    if (
      ingredientCarriers.length !==
      Object.keys(ingredientCarriersStaticInfo).length
    ) {
      return fail(
        new MissingTransportCarrierStaticInfoError(
          difference(
            ingredientCarriers,
            Object.keys(ingredientCarriersStaticInfo)
          )
        )
      );
    }

    return succeed({
      ingredientId: ingredientResponse.ingredientId,
      carriers: ingredientCarriersStaticInfo,
      ports: ingredientPortsStaticInfo,
    });
  }

  function getCarriersFromTransportResponse(
    ingredientResponse: FlightIngredientResponse | FerryIngredientResponse
  ): string[] {
    if (isAvailableTransportIngredientResponse(ingredientResponse)) {
      return getCarriersFromLegs(ingredientResponse.legs);
    } else {
      if (ingredientResponse.alternativeSuggestion) {
        return getCarriersFromLegs(
          ingredientResponse.alternativeSuggestion.legs
        );
      }
    }
    return [];
  }

  function getCarriersFromLegs(legs: TransportLeg[]): string[] {
    const carriers = legs.flatMap((leg) =>
      leg.segments.flatMap((segment) => {
        const carriers = [segment.carrier, segment.operatingCarrier];

        if (leg.platingCarrier) {
          return [...carriers, leg.platingCarrier];
        }
        return carriers;
      })
    );

    return uniq(carriers);
  }

  function getCarriersStaticInfo(
    carriers: string[]
  ): Record<string, CarrierStaticInfo> {
    return staticInfoStore.getCarriersStaticInfo(carriers);
  }

  function getPortsStaticInfo(
    ports: string[],
    kind: IngredientType
  ): Record<string, PortStaticInfo> {
    return staticInfoStore.getPortsStaticInfo(ports, kind);
  }

  function getPortsFromTransportResponse(
    ingredientResponse: FlightIngredientResponse | FerryIngredientResponse
  ): string[] {
    const legs = isAvailableTransportIngredientResponse(ingredientResponse)
      ? ingredientResponse.legs
      : ingredientResponse.alternativeSuggestion?.legs;

    return legs ? getPortsFromLegs(legs) : [];
  }

  function getPortsFromLegs(legs: TransportLeg[]): string[] {
    return legs.flatMap((leg) =>
      leg.segments.flatMap((segment) => [
        segment.departureIata,
        segment.arrivalIata,
      ])
    );
  }

  function getDayAndDatePeriods(
    ingredients: ItineraryMainIngredientResponseWithStaticInfo[],
    startDate: Date,
    tripStartDate: Date
  ): {
    dayPeriod: string | undefined;
    datePeriod: string;
    shortDatePeriod: string;
  } {
    const stayIngredientInGroup = ingredients.find(
      isStayIngredientResponseWithStaticInfo
    );
    const numberOfNights = stayIngredientInGroup?.numberOfNights ?? 0;
    const dayPeriod = getDayPeriodForStop(
      {
        startDate,
        numberOfNights,
        ingredients,
      },
      tripStartDate
    );
    const datePeriod = getDatePeriodForStop({
      format: 'long',
      stop: {
        startDate,
        numberOfNights,
        ingredients,
      },
    });
    const shortDatePeriod = getDatePeriodForStop({
      format: 'short',
      stop: {
        startDate,
        numberOfNights,
        ingredients,
      },
      isSmallDevice: getIsSmallDevice(),
    });
    return { dayPeriod, datePeriod, shortDatePeriod };
  }

  function getCarrierCodesWithStaticInfo(): string[] {
    return staticInfoStore.getCarrierCodesWithStaticInfo();
  }

  function getPortCodesWithStaticInfo(kind: IngredientType): string[] {
    return staticInfoStore.getPortCodesWithStaticInfo(kind);
  }

  function getPortStaticInfo(
    code: string,
    kind: IngredientType
  ): PortStaticInfo | undefined {
    return staticInfoStore.getPortStaticInfo(code, kind);
  }

  function areThereActivitiesInDestination(destinationId: string): boolean {
    const hasDestinationActivities =
      destinationWithAvailableActivities.value[destinationId];

    return !!hasDestinationActivities;
  }

  function isSuggestedHotel(
    ingredientId: IngredientId,
    hotelCode: string
  ): boolean {
    const hotelIngredient =
      editingIngredientStore.editingStayIngredient &&
      isHotelIngredient(editingIngredientStore.editingStayIngredient)
        ? editingIngredientStore.editingStayIngredient
        : recipeStore.getHotelIngredient(ingredientId);

    const thereAreNoHeartedHotels = !hotelIngredient.heartedHotelCodes.length;

    return (
      thereAreNoHeartedHotels && isHotelInItinerary(ingredientId, hotelCode)
    );
  }

  function isHotelInItinerary(
    ingredientId: IngredientId,
    hotelCode: string
  ): boolean {
    const hotelIngredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId);

    const isAvailable =
      !!hotelIngredientResponse &&
      isAvailableHotelIngredientResponse(hotelIngredientResponse);

    const hasSuggestedHotelCode =
      isAvailable && hotelIngredientResponse.suggested.code === hotelCode;

    return hasSuggestedHotelCode;
  }

  function isSuggestedTransport(ingredientId: IngredientId): boolean {
    const transportIngredient =
      editingIngredientStore.editingTransportIngredient ??
      recipeStore.getTransportIngredient(ingredientId);
    return !transportIngredient.transportIds?.length;
  }

  function updateActivitySubIngredients(
    activityCode: string,
    selectedTickets: ActivityTicket[],
    parentIngredientId: IngredientId,
    selectedTicketDate?: string
  ): void {
    const parentIngredient = recipeStore.getStayIngredient(parentIngredientId);

    parentIngredient.subIngredients = parentIngredient.subIngredients.filter(
      (subIngredient) => subIngredient.code !== activityCode
    );

    const selectedTicketsByDateOffset = groupBy(
      selectedTickets,
      (selectedTicket) => selectedTicket.dateOffset.toString()
    );

    for (const dateOffset in selectedTicketsByDateOffset) {
      const tickets = selectedTicketsByDateOffset[dateOffset];
      assertValue(
        tickets,
        'tripState.ts > updateActivitySubIngredients > ticket'
      );
      const getQuantityFromPaxValue = selectedTickets.find(
        (ticket) => ticket.code === activityCode
      )?.getQuantityFromPax;

      const newSubIngredient = new ActivityIngredient(
        activityCode,
        parentIngredientId
      );

      newSubIngredient.offset = Number(dateOffset);
      newSubIngredient.tickets = tickets.map(
        ({ rateId, ageRangeId, quantity, timeSlotId, supplierToken }) => ({
          rateId,
          ageRangeId,
          quantity,
          timeSlotId,
          supplierToken,
          getQuantityFromPax: getQuantityFromPaxValue,
        })
      );
      newSubIngredient.selectedTicketDate = selectedTicketDate;
      parentIngredient.subIngredients.push(newSubIngredient);
    }
  }

  function updateIngredientCommonPolygonText(
    hotelIngredient: HotelIngredient
  ): void {
    const { heartedHotelCodes } = hotelIngredient;

    const commonMinimumPolygon = getCommonMinimumPolygon(
      heartedHotelCodes,
      hotelIngredient.ingredientId
    );

    hotelIngredient.commonPolygon = {
      name: commonMinimumPolygon?.fullName,
      id: commonMinimumPolygon?.polygonId,
    };
  }

  function updateIngredientText(hotelIngredient: HotelIngredient): void {
    // Keep the meta hotel's specific-hotel search; don't broaden it to its polygon.
    if (
      hotelIngredient.searchTerm.type === SearchTermType.HotelCode &&
      metaStore.metaHotelCode === hotelIngredient.searchTerm.hotelCode
    ) {
      return;
    }

    const newPolygonId =
      hotelIngredient.nearbyPolygon?.id ?? hotelIngredient.commonPolygon?.id;

    if (newPolygonId) {
      hotelIngredient.setSearchTerm({
        type: SearchTermType.PolygonId,
        polygonId: newPolygonId,
      });
    }
  }

  function updateSessionId(value: string): void {
    sessionId.value = value;
  }

  function updateDestinationStaticInfo(): void {
    destinationStaticInfo.value = Object.assign(newDestinationStaticInfo.value);
  }

  function updateAreAllSelectedRatesValid(value: boolean): void {
    areAllSelectedRatesValid.value = value;
  }

  function updateEndLocationIsManuallyTyped(value: boolean): void {
    endLocationIsManuallyTyped.value = value;
  }

  function updateStaysShown(value: number): void {
    staysShown.value = value;
  }

  function updateDestinationAvailability(): void {
    destinationAvailability.value = Object.assign(
      newDestinationAvailability.value
    );
  }

  function updateFerriesVehicleTypes(
    newFerriesVehicleTypes: TransportVehicle[]
  ): void {
    ferryVehicleTypes.value = newFerriesVehicleTypes;
  }

  function updateDestinationWithAvailableActivities(
    destinationId: string,
    availableActivities: boolean
  ): void {
    destinationWithAvailableActivities.value[destinationId] =
      availableActivities;
  }

  function toggleHeartHotel(
    heartedHotel: HeartedHotel,
    ingredientId: IngredientId
  ): void {
    const provisionalHeartedHotel: HeartedHotel = { ...heartedHotel };
    const ingredient = recipeStore.getStayIngredient(ingredientId);

    if (isHotelIngredient(ingredient)) {
      const newHeartedHotels = [...ingredient.heartedHotels];

      const heartedHotelPredicate = (existing: HeartedHotel): boolean =>
        provisionalHeartedHotel.code === existing.code;

      const roomCodes =
        heartedHotel.rooms?.map((room) => room.code) ?? heartedHotel.roomCodes;

      const shouldDeleteHearted =
        ingredient.heartedHotels.includes(provisionalHeartedHotel) ||
        (ingredient.heartedHotels.length && !roomCodes?.length);

      const toggleMode = shouldDeleteHearted
        ? ToggleMode.Delete
        : ToggleMode.Replace;

      replaceOrDeleteValues(
        provisionalHeartedHotel,
        newHeartedHotels,
        heartedHotelPredicate,
        toggleMode
      );

      ingredient.heartedHotels = newHeartedHotels;
      updateIngredientCommonPolygonText(ingredient);
      updateIngredientText(ingredient);
    }

    if (isOwnStayIngredient(ingredient)) {
      const newHotelIngredient = new HotelIngredient(
        ingredient.searchTerm,
        false
      );

      newHotelIngredient.heartedHotels = [{ ...heartedHotel }];
      recipeStore.replaceIngredient(ingredient, newHotelIngredient);
    }
  }

  async function toggleExpandedMonth(month: Date): Promise<void> {
    expandMonth(month);

    if (expandedMonth.value) {
      const isCurrentMonth = isDateInExpandedMonth(new Date());

      const monthHasAlreadyOpened =
        startSearchDate.value && isDateInExpandedMonth(startSearchDate.value);

      if (!monthHasAlreadyOpened || startSearchDate.value === undefined) {
        if (!isCurrentMonth) {
          trackMonthChanged();
        }
        clearPreferredDay();
        startSearchDate.value = isCurrentMonth
          ? new Date()
          : new SemboDate(expandedMonth.value).startOf('month').get();

        endSearchDate.value = new SemboDate(expandedMonth.value)
          .endOf('month')
          .get();
      }

      await nextTick();

      const timePerDate = 20;
      const selectedElement = document.querySelector<HTMLElement>(
        '.alternative-itinerary-month.selected'
      );
      const datesInMonth = itineraryStore.itineraryDates.length;
      const timePerMonth = `${timePerDate * datesInMonth}ms`;
      if (selectedElement) {
        selectedElement.style.animationDuration = timePerMonth;
      }
    }
  }

  function hotelIsHearted(
    hotelCode: string,
    parentIngredientId: IngredientId
  ): boolean {
    const parentIngredient = recipeStore.getStayIngredient(parentIngredientId);

    return (
      isHotelIngredient(parentIngredient) &&
      parentIngredient.heartedHotelCodes.includes(hotelCode)
    );
  }

  function clearDestinationStaticInfo(): void {
    destinationStaticInfo.value = {};
  }

  function clearNewDestinationStaticInfo(): void {
    newDestinationStaticInfo.value = {};
  }

  function clearDestinationAvailability(): void {
    destinationAvailability.value = {};
  }

  function clearHotelSelectionItinerary(): void {
    hotelSelectionItinerary.value = undefined;
  }

  function setHotelSelectionItinerary(itinerary: ItineraryWithPrice): void {
    hotelSelectionItinerary.value = itinerary;
  }

  function clearNewDestinationAvailability(): void {
    newDestinationAvailability.value = {};
  }

  function clearTransportList(): void {
    setTransportList([]);
  }

  function setFlightStaticInfoToRecipe(): void {
    recipeStore.flightOnlyRecipe.forEach((flightIngredient) => {
      assertValue(
        flightIngredient.fromPort,
        'tripState > setFlightStaticInfoToRecipe > fromPort'
      );
      assertValue(
        flightIngredient.toPort,
        'tripState > setFlightStaticInfoToRecipe > toPort'
      );

      const fromPortStaticInfo = getPortStaticInfo(
        flightIngredient.fromPort,
        IngredientType.Flight
      );
      const toPortStaticInfo = getPortStaticInfo(
        flightIngredient.toPort,
        IngredientType.Flight
      );

      flightIngredient.fromPortText = fromPortStaticInfo?.position.name;
      flightIngredient.fromPortDisplayText = fromPortStaticInfo?.name;
      flightIngredient.toPortText = toPortStaticInfo?.position.name;
      flightIngredient.toPortDisplayText = toPortStaticInfo?.name;
    });
  }

  function setTransportList(
    newTransportList: AvailableTransportIngredientResponseWithStaticInfo[],
    selected?: AvailableTransportIngredientResponseWithStaticInfo
  ): void {
    if (selected) {
      transportList.value = [
        selected,
        ...newTransportList.filter(
          (transport) =>
            transport.addToBasketToken !== selected?.addToBasketToken
        ),
      ];
    } else {
      transportList.value = newTransportList;
    }
  }

  function setFerryStaticInfoToRecipe(): void {
    recipeStore.ferryOnlyRecipe.forEach((ferryIngredient) => {
      const ferryFromPort = ferryIngredient.fromPort;
      const ferryToPort = ferryIngredient.toPort;
      assertValue(
        ferryFromPort,
        'tripState > setFerryStaticInfoToRecipe > ferryFromPort'
      );
      assertValue(
        ferryToPort,
        'tripState > setFerryStaticInfoToRecipe > ferryToPort'
      );

      const fromPort = Object.keys(staticInfoStore.seaportStaticInfo).find(
        (key) => key.includes(ferryFromPort)
      );
      const toPort = Object.keys(staticInfoStore.seaportStaticInfo).find(
        (key) => key.includes(ferryToPort)
      );

      assertValue(
        fromPort,
        'tripState > setFerryStaticInfoToRecipe > fromPort'
      );
      assertValue(toPort, 'tripState > setFerryStaticInfoToRecipe > toPort');
      const fromPortStaticInfo = getPortStaticInfo(
        fromPort,
        IngredientType.Ferry
      );
      const toPortStaticInfo = getPortStaticInfo(toPort, IngredientType.Ferry);

      ferryIngredient.fromPortText = fromPortStaticInfo?.position.name;
      ferryIngredient.fromPortDisplayText = fromPortStaticInfo?.name;
      ferryIngredient.toPortText = toPortStaticInfo?.position.name;
      ferryIngredient.toPortDisplayText = toPortStaticInfo?.name;
    });
  }

  function replaceHeartedHotel(
    heartedHotel: HeartedHotel,
    ingredientId: IngredientId
  ): void {
    const ingredient = recipeStore.getHotelIngredient(ingredientId);
    ingredient.heartedHotels = [heartedHotel];
    updateIngredientCommonPolygonText(ingredient);
    updateIngredientText(ingredient);
  }

  function insertNewDestinationAvailability(
    availability: ProductCheapestAvailabilityResponse[]
  ): void {
    availability.forEach((productAvailability) => {
      newDestinationAvailability.value[productAvailability.placeCode] =
        productAvailability;
      destinationAvailability.value[productAvailability.placeCode] =
        productAvailability;
    });
  }

  function increaseDestinationAvailabilitySearchCounter(): void {
    destinationAvailabilitySearchCounter.value++;
  }

  function increaseHotelSelectionItinerarySearchCounter(): void {
    hotelSelectionItinerarySearchCounter.value++;
  }

  function decreaseHotelSelectionItinerarySearchCounter(): void {
    hotelSelectionItinerarySearchCounter.value--;
  }

  function increaseHotelSelectionStaticInfoSearchCounter(): void {
    hotelSelectionStaticInfoSearchCounter.value++;
  }

  function decreaseHotelSelectionStaticInfoSearchCounter(): void {
    hotelSelectionStaticInfoSearchCounter.value--;
  }

  function insertNewDestinationStaticInfo(
    newStaticInfo: Record<string, DestinationStaticInfo>
  ): void {
    Object.assign(newDestinationStaticInfo.value, newStaticInfo);
    Object.assign(destinationStaticInfo.value, newStaticInfo);
  }

  function sortEntriesByDate(
    a: [string, ItineraryMainIngredientResponseWithStaticInfo[]],
    b: [string, ItineraryMainIngredientResponseWithStaticInfo[]]
  ): number {
    const baseA = a[0].replace(/-\d+$/, '');
    const baseB = b[0].replace(/-\d+$/, '');
    if (baseA !== baseB) {
      return baseA.localeCompare(baseB);
    }
    return a[0].length === b[0].length
      ? a[0].localeCompare(b[0])
      : a[0].length > b[0].length
        ? -1
        : 1;
  }

  function sortIngredientsByTransport(
    ingredients: ItineraryMainIngredientResponseWithStaticInfo[]
  ): ItineraryMainIngredientResponseWithStaticInfo[] {
    return [...ingredients].sort((a, b) => {
      const isTransportA =
        isAvailableTransportIngredientResponseWithStaticInfo(a);
      const isTransportB =
        isAvailableTransportIngredientResponseWithStaticInfo(b);

      if (isTransportA && isTransportB) {
        return new SemboDate(a.departureDate).diff(
          new SemboDate(b.departureDate).get(),
          'second'
        );
      }

      return 0;
    });
  }

  function addUnknownOwnTransport(
    ingredients: ItineraryMainIngredientResponseWithStaticInfo[]
  ): ItineraryMainIngredientResponseWithStaticInfo[] {
    if (ingredients.length === recipeStore.recipe.length) {
      return ingredients;
    }
    // NOTE: this is just for test purposes, improve and add test if we want to go to production with this
    let ingIndex = 0;

    return recipeStore.recipe.reduce<
      ItineraryMainIngredientResponseWithStaticInfo[]
    >((result, recipeItem) => {
      const currentIngredient = ingredients[ingIndex];

      if (
        currentIngredient &&
        currentIngredient.ingredientId === recipeItem.ingredientId
      ) {
        result.push(currentIngredient);
        ingIndex++;
      } else if (
        isOwnTransportIngredient(recipeItem) &&
        recipeItem.ownTransportType === TransportType.None
      ) {
        const previousItineraryIngredient = ingredients[ingIndex - 1];
        const nextItineraryIngredient = ingredients[ingIndex + 1];

        const isLastIngredientDate =
          previousItineraryIngredient?.date &&
          isStayIngredientResponseWithStaticInfo(previousItineraryIngredient) &&
          previousItineraryIngredient.numberOfNights
            ? new SemboDate(previousItineraryIngredient.date)
                .add({ days: previousItineraryIngredient.numberOfNights })
                .get()
            : previousItineraryIngredient?.date;

        const date =
          ingredients[ingIndex]?.date ??
          isLastIngredientDate ??
          nextItineraryIngredient?.date ??
          new Date();

        result.push({
          ingredientId: recipeItem.ingredientId,
          ingredientType: recipeItem.ingredientType,
          date: date,
          isAvailable: true,
          ownTransportType: recipeItem.ownTransportType,
          staticInfo: null,
        });
      }
      return result;
    }, []);
  }

  function decreaseDestinationAvailabilitySearchCounter(): void {
    destinationAvailabilitySearchCounter.value--;
  }

  function increaseStaticDestinationAvailabilitySearchCounter(): void {
    staticDestinationAvailabilitySearchCounter.value++;
  }

  function stayIsLocked(
    stay: HotelBookableOptionWithRooms,
    ingredientId?: IngredientId
  ): boolean | undefined {
    if (!ingredientId) {
      return false;
    }
    const ingredient = recipeStore.getStayIngredient(ingredientId);

    if (isOwnStayIngredient(ingredient)) {
      return false;
    }

    const heartedHotel = ingredient.heartedHotels.find(
      (hotel) => hotel.code === stay.code
    );

    if (!heartedHotel || heartedHotel.isRefundable !== stay.isRefundable) {
      return false;
    }

    const heartedRoomMatchers = getHeartedRoomMatchers(heartedHotel);

    return heartedRoomMatchers?.every((heartedRoom) =>
      stay.rooms.some((stayRoom) =>
        stayRoomMatchesHeartedRoom(stayRoom, heartedRoom)
      )
    );
  }

  function increaseTransportListSearchCounter(): void {
    transportListSearchCounter.value++;
  }

  function decreaseStaticDestinationAvailabilitySearchCounter(): void {
    staticDestinationAvailabilitySearchCounter.value--;
  }

  function decreaseTransportListSearchCounter(): void {
    transportListSearchCounter.value--;
  }

  return {
    // State
    months: readonlyRef(months),
    areAllSelectedRatesValid: readonlyRef(areAllSelectedRatesValid),
    ferryVehicleTypes: readonlyRef(ferryVehicleTypes),
    sessionId: readonlyRef(sessionId),
    isLocationManuallyChanged: readonlyRef(isLocationManuallyChanged),
    startingLocationIsManuallyTyped: readonlyRef(
      startingLocationIsManuallyTyped
    ),
    airportStaticInfo: staticInfoStore.airportStaticInfo,
    destinationStaticInfo,
    hotelSelectionItinerary: readonlyRef(hotelSelectionItinerary),

    // Getters - computed
    chronologicalItinerary,
    destinationProducts,
    isSearchingDestinationAvailability,
    isSearchingHotelSelectionItinerary,
    isSearchingTransportList,
    preselectedHotel,
    selectedItineraryWithStaticInfo,
    selectedItineraryWithStaticInfoResults,
    travelDateRange,

    // Getters - functions
    areThereActivitiesInDestination,
    getCarrierCodesWithStaticInfo,
    getCarriersStaticInfo,
    getDatePeriodForStop,
    getDayPeriodForStop,
    getFerryAllCarriers,
    getFerryAllCarrierCodes,
    getFlightAllCarrierCodes,
    getFlightAllCarriers,
    getFlightRanges,
    getMainPolygonFullName,
    getMainPolygonId,
    getOverviewStaticInfo,
    getPortCodesWithStaticInfo,
    getPortStaticInfo,
    getStaticInfo,
    getTransportStaticInfoResult,
    hotelIsHearted,
    isHotelInItinerary,
    isSuggestedHotel,
    isSuggestedTransport,
    stayIsLocked,

    // Actions
    clearDestinationAvailability,
    clearDestinationStaticInfo,
    clearHotelSelectionItinerary,
    clearNewDestinationAvailability,
    clearNewDestinationStaticInfo,
    clearTransportList,
    decreaseDestinationAvailabilitySearchCounter,
    decreaseHotelSelectionItinerarySearchCounter,
    decreaseHotelSelectionStaticInfoSearchCounter,
    decreaseStaticDestinationAvailabilitySearchCounter,
    decreaseTransportListSearchCounter,
    increaseDestinationAvailabilitySearchCounter,
    increaseHotelSelectionItinerarySearchCounter,
    increaseHotelSelectionStaticInfoSearchCounter,
    increaseStaticDestinationAvailabilitySearchCounter,
    increaseTransportListSearchCounter,
    insertNewDestinationAvailability,
    insertNewDestinationStaticInfo,
    replaceHeartedHotel,
    setFerryStaticInfoToRecipe,
    setFlightStaticInfoToRecipe,
    setHotelSelectionItinerary,
    setTransportList,
    toggleExpandedMonth,
    toggleHeartHotel,
    updateActivitySubIngredients,
    updateAreAllSelectedRatesValid,
    updateDestinationAvailability,
    updateDestinationStaticInfo,
    updateDestinationWithAvailableActivities,
    updateEndLocationIsManuallyTyped,
    updateFerriesVehicleTypes,
    updateSessionId,
    updateStaysShown,
  };
});
